import React, { useState, useEffect } from "react";
import { Button, Card } from "react-bootstrap";
import "./styles.scss";
import BsChevronUp from "../../../../../../constants/icons/Bs/BsChevronUp";
import BsChevronDown from "../../../../../../constants/icons/Bs/BsChevronDown";
import { BsBook } from "../../../../../../constants/icons/Bs/BsBook";
import BsTask from "../../../../../../constants/icons/Bs/BsTask";
import Tooltip from "../Tooltip";

const Recommendations = ({ recommendations, handleResourceClick }) => {
  const [expandedSections, setExpandedSections] = useState({});
  const [activeTab, setActiveTab] = useState("recommendations");
  const [activePriority, setActivePriority] = useState("all");
  const [activeType, setActiveType] = useState("all");
  const [activeAttempt, setActiveAttempt] = useState("all");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);

  const recommendationsTooltip = (
    <>
      <h4>Top Recommendations</h4>
      <p>
        These are personalized recommendations based on your current progress
        and performance.
      </p>
      <p>
        <strong>How to use this section:</strong>
      </p>
      <ol>
        <li>Use filters to find specific types of resources</li>
        <li>Priority levels indicate how important each resource is</li>
        <li>Click on any resource to start practicing</li>
        <li>Complete both practice sets and tests for best results</li>
      </ol>
      <p>
        <strong>Tip:</strong> Start with high-priority resources to maximize
        your learning efficiency!
      </p>
    </>
  );

  const championTooltip = (
    <>
      <h4>Champion Level Topics</h4>
      <p>
        These are topics where you've achieved champion level (90% or higher).
      </p>
      <p>
        <strong>What this means:</strong>
      </p>
      <ul>
        <li>You've demonstrated excellent understanding of these topics</li>
        <li>You can still practice to maintain your skills</li>
        <li>Consider helping others with these topics</li>
      </ul>
      <p>
        <strong>Tip:</strong> Regular practice of champion topics helps maintain
        your mastery!
      </p>
    </>
  );

  // Detect screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Transform recommendations data to group by topic
  const transformedRecommendations = recommendations.map((topic) => {
    const score = parseFloat(topic.score);
    const resources = { unattempted: [], attempted: [] };

    const hasUnattemptedResources =
      (topic.unAttemptedPractices && topic.unAttemptedPractices.length > 0) ||
      (topic.unAttemptedTests && topic.unAttemptedTests.length > 0);

    if (hasUnattemptedResources || (score < 90 && !hasUnattemptedResources)) {
      if (topic.unAttemptedPractices && topic.unAttemptedPractices.length > 0) {
        resources.unattempted.push(
          ...topic.unAttemptedPractices.map((practice) => ({
            id: practice._id,
            name: practice.name,
            type: "practice",
            examType: practice?.type,
            topic: {
              id: topic._id,
              name: topic.name,
              subjectId: topic.subjectId,
              score,
            },
            priority: "High",
            progress: score,
            attempted: false,
          }))
        );
      }

      if (topic.unAttemptedTests && topic.unAttemptedTests.length > 0) {
        resources.unattempted.push(
          ...topic.unAttemptedTests.map((test) => ({
            id: test._id,
            name: test.name,
            type: "test",
            examType: test?.type,
            topic: {
              id: topic._id,
              name: topic.name,
              subjectId: topic.subjectId,
              score,
            },
            priority: "High",
            progress: score,
            attempted: false,
          }))
        );
      }

      if (topic.attemptedPractices && topic.attemptedPractices.length > 0) {
        resources.attempted.push(
          ...topic.attemptedPractices
            .filter((practice) => {
              const practiceScore = practice?.type === 'speaking' || practice?.type === 'writing' ?
                ((practice?.score ?? 0) / 9) * 100
                :
                (practice.obtainedPoints / practice.totalPoints) * 100;
              return practiceScore < 90;
            })
            .map((practice) => {
              const practiceScore = practice?.type === 'speaking' || practice?.type === 'writing' ?
                ((practice?.score ?? 0) / 9) * 100
                :
                (practice.obtainedPoints / practice.totalPoints) * 100;
              let priority = "Low";
              if (practiceScore >= 70) priority = "High";
              else if (practiceScore >= 30) priority = "Medium";
              return {
                id: practice._id,
                name: practice.name,
                type: "practice",
                examType: practice?.type,
                isIeltsSet: practice?.type === 'speaking' || practice?.type === 'writing',
                score: practice.score ?? 0,
                topic: {
                  id: topic._id,
                  name: topic.name,
                  subjectId: topic.subjectId,
                  score,
                },
                priority,
                progress: practiceScore,
                attempted: true,
                obtainedPoints: practice.obtainedPoints,
                totalPoints: practice.totalPoints,
              };
            })
        );
      }

      if (topic.attemptedTests && topic.attemptedTests.length > 0) {
        resources.attempted.push(
          ...topic.attemptedTests
            .filter((test) => {
              const testScore = test?.type === 'speaking' || test?.type === 'writing' ?
                ((test?.score ?? 0) / 9) * 100
                :
                (test.obtainedPoints / test.totalPoints) * 100;
              return testScore < 90;
            })
            .map((test) => {
              const testScore = test?.type === 'speaking' || test?.type === 'writing' ?
                (test?.score ?? 0 / 9) * 100
                :
                (test.obtainedPoints / test.totalPoints) * 100;
              let priority = "Low";
              if (testScore >= 70) priority = "High";
              else if (testScore >= 30) priority = "Medium";
              return {
                id: test._id,
                name: test.name,
                type: "test",
                examType: test?.type,
                isIeltsSet: test?.type === 'speaking' || test?.type === 'writing',
                score: test.score ?? 0,
                topic: {
                  id: topic._id,
                  name: topic.name,
                  subjectId: topic.subjectId,
                  score,
                },
                priority,
                progress: testScore,
                attempted: true,
                obtainedPoints: test.obtainedPoints,
                totalPoints: test.totalPoints,
              };
            })
        );
      }
    }

    return {
      topic: {
        id: topic._id,
        name: topic.name,
        score,
        subjectId: topic.subjectId,
      },
      resources,
    };
  });

  const filteredForRecommendations = transformedRecommendations.filter(
    ({ topic, resources }) =>
      resources.unattempted.length > 0 || resources.attempted.length > 0
  );

  const sortedRecommendations = [...filteredForRecommendations].sort(
    (a, b) => b.topic.score - a.topic.score
  );

  const filteredRecommendations = sortedRecommendations.map(
    ({ topic, resources }) => ({
      topic,
      resources: {
        unattempted: resources.unattempted.filter(
          (resource) =>
            (activePriority === "all" ||
              resource.priority === activePriority) &&
            (activeType === "all" || resource.type === activeType) &&
            (activeAttempt === "all" || activeAttempt === "unattempted")
        ),
        attempted: resources.attempted.filter(
          (resource) =>
            (activePriority === "all" ||
              resource.priority === activePriority) &&
            (activeType === "all" || resource.type === activeType) &&
            (activeAttempt === "all" || activeAttempt === "attempted")
        ),
      },
    })
  ).filter(({resources}) => resources?.unattempted?.length > 0 || resources?.attempted?.length > 0);

  const toggleSection = (topicId, sectionType) => {
    setExpandedSections((prev) => ({
      ...prev,
      [`${topicId}-${sectionType}`]: !prev[`${topicId}-${sectionType}`],
    }));
  };

  return (
    <div className="recommendations">
      <div className="section-header">
        <div>
          <h2>Top Recommendations</h2>
          <p>Complete these topics to improve your performance</p>
        </div>
      </div>

      <div className="recommendations-tabs">
        <button
          className={`tab-button ${
            activeTab === "recommendations" ? "active" : ""
          }`}
          onClick={() => setActiveTab("recommendations")}
        >
          Recommendations
          {activeTab === "recommendations" && (
            <Tooltip content={recommendationsTooltip} position="bottom" />
          )}
        </button>
        <button
          className={`tab-button ${activeTab === "champion" ? "active" : ""}`}
          onClick={() => setActiveTab("champion")}
        >
          Champion Level Topics
          {activeTab === "champion" && (
            <Tooltip content={championTooltip} position="bottom" />
          )}
        </button>
      </div>

      {activeTab === "recommendations" && (
        <>
          <div
            className={`filters-container ${isMobile ? "mobile-filters" : ""}`}
          >
            <div className="filter-section">
              <div className="filter-label">Priority</div>
              <div className="priority-filter">
                <button
                  className={`filter-button ${
                    activePriority === "all" ? "active" : ""
                  }`}
                  onClick={() => setActivePriority("all")}
                >
                  All
                </button>
                <button
                  className={`filter-button ${
                    activePriority === "High" ? "active" : ""
                  }`}
                  onClick={() => setActivePriority("High")}
                >
                  High
                </button>
                <button
                  className={`filter-button ${
                    activePriority === "Medium" ? "active" : ""
                  }`}
                  onClick={() => setActivePriority("Medium")}
                >
                  Medium
                </button>
                <button
                  className={`filter-button ${
                    activePriority === "Low" ? "active" : ""
                  }`}
                  onClick={() => setActivePriority("Low")}
                >
                  Low
                </button>
              </div>
            </div>

            <div className="filter-section">
              <div className="filter-label">Type</div>
              <div className="type-filter">
                <button
                  className={`filter-button ${
                    activeType === "all" ? "active" : ""
                  }`}
                  onClick={() => setActiveType("all")}
                >
                  All
                </button>
                <button
                  className={`filter-button ${
                    activeType === "practice" ? "active" : ""
                  }`}
                  onClick={() => setActiveType("practice")}
                >
                  Practice Sets
                </button>
                <button
                  className={`filter-button ${
                    activeType === "test" ? "active" : ""
                  }`}
                  onClick={() => setActiveType("test")}
                >
                  Tests
                </button>
              </div>
            </div>

            <div className="filter-section">
              <div className="filter-label">Status</div>
              <div className="attempt-filter">
                <button
                  className={`filter-button ${
                    activeAttempt === "all" ? "active" : ""
                  }`}
                  onClick={() => setActiveAttempt("all")}
                >
                  All
                </button>
                <button
                  className={`filter-button ${
                    activeAttempt === "attempted" ? "active" : ""
                  }`}
                  onClick={() => setActiveAttempt("attempted")}
                >
                  Attempted
                </button>
                <button
                  className={`filter-button ${
                    activeAttempt === "unattempted" ? "active" : ""
                  }`}
                  onClick={() => setActiveAttempt("unattempted")}
                >
                  Unattempted
                </button>
              </div>
            </div>
          </div>

          <div className="topics-container">
            {filteredRecommendations.map(({ topic, resources }) => (
              <Card key={topic.id} className="topic-card">
                <Card.Body className="topic-card-body">
                  <div className="topic-header">
                    <h3 className="topic-name" title={topic.name}>
                      {topic.name.length > (isMobile ? 20 : 40)
                        ? `${topic.name.substring(0, isMobile ? 20 : 40)}...`
                        : topic.name}
                    </h3>
                    <div className="completion-info">
                      <div className="completion-badge">
                        {topic.score.toFixed(1)}% Complete
                      </div>
                      <div className="progress-container">
                        <div
                          className="progress-fill"
                          style={{ width: `${topic.score}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="content-sections">
                    {resources.unattempted.length > 0 && (
                      <div className="content-section">
                        <div
                          className="section-header"
                          onClick={() => toggleSection(topic.id, "unattempted")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="section-title">
                            <h4>
                              Unattempted Resources (
                              {resources.unattempted.length})
                            </h4>
                          </div>
                          <div className="section-toggle">
                            {expandedSections[`${topic.id}-unattempted`] ? (
                              <BsChevronUp className="toggle-icon" />
                            ) : (
                              <BsChevronDown className="toggle-icon" />
                            )}
                          </div>
                        </div>
                        {expandedSections[`${topic.id}-unattempted`] && (
                          <div className="items-container">
                            {resources.unattempted.map((resource) => (
                              <div key={resource.id} className="item">
                                <div className="item-details">
                                  <span className="item-type">
                                    {resource.type === "practice" ? (
                                      <>
                                        <BsBook className="type-icon practice-icon" />
                                        <span className="type-label">
                                          Practice Set
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <BsTask className="type-icon test-icon" />
                                        <span className="type-label">Test</span>
                                      </>
                                    )}
                                  </span>
                                  <span
                                    className="item-name"
                                    title={resource.name}
                                  >
                                    {resource.name.length > (isMobile ? 20 : 40)
                                      ? `${resource.name.substring(
                                          0,
                                          isMobile ? 20 : 40
                                        )}...`
                                      : resource.name}
                                  </span>
                                  <span
                                    className={`priority-badge ${resource.priority.toLowerCase()}`}
                                  >
                                    {resource.priority} Priority
                                  </span>
                                </div>
                                <Button
                                  variant="outline-primary"
                                  className="action-button"
                                  onClick={() =>
                                    handleResourceClick(
                                      resource,
                                      resource.topic
                                    )
                                  }
                                >
                                  Start
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {resources.attempted.length > 0 && (
                      <div className="content-section">
                        <div
                          className="section-header"
                          onClick={() => toggleSection(topic.id, "attempted")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="section-title">
                            <h4>
                              Attempted Resources ({resources.attempted.length})
                            </h4>
                          </div>
                          <div className="section-toggle">
                            {expandedSections[`${topic.id}-attempted`] ? (
                              <BsChevronUp className="toggle-icon" />
                            ) : (
                              <BsChevronDown className="toggle-icon" />
                            )}
                          </div>
                        </div>
                        {expandedSections[`${topic.id}-attempted`] && (
                          <div className="items-container">
                            {resources.attempted.map((resource) => (
                              <div key={resource.id} className="item">
                                <div className="item-details">
                                  <span className="item-type">
                                    {resource.type === "practice" ? (
                                      <>
                                        <BsBook className="type-icon practice-icon" />
                                        <span className="type-label">
                                          Practice Set
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <BsTask className="type-icon test-icon" />
                                        <span className="type-label">Test</span>
                                      </>
                                    )}
                                  </span>
                                  <span
                                    className="item-name"
                                    title={resource.name}
                                  >
                                    {resource.name.length > (isMobile ? 20 : 40)
                                      ? `${resource.name.substring(
                                          0,
                                          isMobile ? 20 : 40
                                        )}...`
                                      : resource.name}
                                  </span>
                                  <span
                                    className={`priority-badge ${resource.priority.toLowerCase()}`}
                                  >
                                    {resource.priority} Priority
                                  </span>
                                  <span className="score-badge">
                                    {resource?.isIeltsSet ? `
                                    Band: ${resource.score} (
                                    ${((resource.score/9) * 100).toFixed(1)}%)` : `
                                    Score: ${resource.obtainedPoints}/
                                    ${resource.totalPoints} (
                                    ${resource.progress.toFixed(1)}%)`}
                                  </span>
                                </div>
                                <Button
                                  variant="outline-primary"
                                  className="action-button"
                                  onClick={() =>
                                    handleResourceClick(
                                      resource,
                                      resource.topic
                                    )
                                  }
                                >
                                  Reattempt
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            ))}
            {!filteredRecommendations.length && (<div style={{
              fontWeight: '800',
              fontSize: '16px',
              textAlign: 'center'
            }}>No Topic found.</div>)}
          </div>
        </>
      )}

      {activeTab === "champion" && (
        <div className="champion-topics">
          <div className="topics-container">
            {recommendations.filter((topic) => {
              const score = parseFloat(topic.score);
              const hasUnattemptedResources =
                (topic.unAttemptedPractices &&
                  topic.unAttemptedPractices.length > 0) ||
                (topic.unAttemptedTests && topic.unAttemptedTests.length > 0);
              return score >= 90 && !hasUnattemptedResources;
            }).length === 0 ? (
              <div
                className="no-champion-message"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "40px 0",
                  color: "#6c63ff",
                  background: "#f8f9fa",
                  borderRadius: "12px",
                  minHeight: "220px",
                  fontSize: "1.2rem",
                  fontWeight: 500,
                }}
              >
                <svg
                  width="80"
                  height="80"
                  viewBox="0 0 80 80"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="40" cy="40" r="40" fill="#E0E7FF" />
                  <path d="M40 20L50 40H30L40 20Z" fill="#6C63FF" />
                  <rect
                    x="35"
                    y="40"
                    width="10"
                    height="20"
                    rx="5"
                    fill="#6C63FF"
                  />
                  <circle cx="40" cy="65" r="5" fill="#6C63FF" />
                </svg>
                <div style={{ marginTop: 24, textAlign: "center" }}>
                  <div style={{ fontSize: "1.3rem", fontWeight: 600 }}>
                    No Champion Level Topics Yet
                  </div>
                  <div style={{ marginTop: 8, color: "#555", fontWeight: 400 }}>
                    Keep practicing and attempting all resources.
                    <br />
                    Once you achieve 90% or higher in a topic and complete all
                    its tests and practice sets, it will appear here as a
                    Champion!
                  </div>
                </div>
              </div>
            ) : (
              recommendations
                .filter((topic) => {
                  const score = parseFloat(topic.score);
                  const hasUnattemptedResources =
                    (topic.unAttemptedPractices &&
                      topic.unAttemptedPractices.length > 0) ||
                    (topic.unAttemptedTests &&
                      topic.unAttemptedTests.length > 0);
                  return score >= 90 && !hasUnattemptedResources;
                })
                .map((topic) => (
                  <Card key={topic._id} className="topic-card">
                    <Card.Body className="topic-card-body">
                      <div className="topic-header">
                        <h3 className="topic-name" title={topic.name}>
                          {topic.name.length > (isMobile ? 20 : 40)
                            ? `${topic.name.substring(
                                0,
                                isMobile ? 20 : 40
                              )}...`
                            : topic.name}
                        </h3>
                        <div className="completion-info">
                          <div className="completion-badge champion">
                            {topic.score.toFixed(1)}% Complete
                          </div>
                          <div className="progress-container">
                            <div
                              className="progress-fill champion"
                              style={{ width: `${topic.score}%` }}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="content-sections">
                        {topic.attemptedPractices &&
                          topic.attemptedPractices.length > 0 && (
                            <div className="content-section">
                              <div
                                className="section-header"
                                onClick={() =>
                                  toggleSection(topic._id, "champion-practices")
                                }
                                style={{ cursor: "pointer" }}
                              >
                                <div className="section-title">
                                  <h4>
                                    Practice Sets (
                                    {topic.attemptedPractices.length})
                                  </h4>
                                </div>
                                <div className="section-toggle">
                                  {expandedSections[
                                    `${topic._id}-champion-practices`
                                  ] ? (
                                    <BsChevronUp className="toggle-icon" />
                                  ) : (
                                    <BsChevronDown className="toggle-icon" />
                                  )}
                                </div>
                              </div>
                              {expandedSections[
                                `${topic._id}-champion-practices`
                              ] && (
                                <div className="items-container">
                                  {topic.attemptedPractices.map((practice) => {
                                    const practiceScore =
                                      (practice.obtainedPoints /
                                        practice.totalPoints) *
                                      100;
                                    return (
                                      <div key={practice._id} className="item">
                                        <div className="item-details">
                                          <span className="item-type">
                                            <BsBook className="type-icon practice-icon" />
                                            <span className="type-label">
                                              Practice Set
                                            </span>
                                          </span>
                                          <span
                                            className="item-name"
                                            title={practice.name}
                                          >
                                            {practice.name.length >
                                            (isMobile ? 20 : 40)
                                              ? `${practice.name.substring(
                                                  0,
                                                  isMobile ? 20 : 40
                                                )}...`
                                              : practice.name}
                                          </span>
                                          <span className="score-badge champion">
                                            Score: {practice.obtainedPoints}/
                                            {practice.totalPoints} (
                                            {practiceScore.toFixed(1)}%)
                                          </span>
                                        </div>
                                        <Button
                                          variant="outline-primary"
                                          className="action-button"
                                          onClick={() =>
                                            handleResourceClick(
                                              {
                                                id: practice._id,
                                                name: practice.name,
                                                type: "practice",
                                                topic: {
                                                  id: topic._id,
                                                  name: topic.name,
                                                  subjectId: topic.subjectId,
                                                  score: topic.score,
                                                },
                                              },
                                              {
                                                id: topic._id,
                                                name: topic.name,
                                                subjectId: topic.subjectId,
                                              }
                                            )
                                          }
                                        >
                                          Reattempt
                                        </Button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}

                        {topic.attemptedTests &&
                          topic.attemptedTests.length > 0 && (
                            <div className="content-section">
                              <div
                                className="section-header"
                                onClick={() =>
                                  toggleSection(topic._id, "champion-tests")
                                }
                                style={{ cursor: "pointer" }}
                              >
                                <div className="section-title">
                                  <h4>Tests ({topic.attemptedTests.length})</h4>
                                </div>
                                <div className="section-toggle">
                                  {expandedSections[
                                    `${topic._id}-champion-tests`
                                  ] ? (
                                    <BsChevronUp className="toggle-icon" />
                                  ) : (
                                    <BsChevronDown className="toggle-icon" />
                                  )}
                                </div>
                              </div>
                              {expandedSections[
                                `${topic._id}-champion-tests`
                              ] && (
                                <div className="items-container">
                                  {topic.attemptedTests.map((test) => {
                                    const testScore =
                                      (test.obtainedPoints / test.totalPoints) *
                                      100;
                                    return (
                                      <div key={test._id} className="item">
                                        <div className="item-details">
                                          <span className="item-type">
                                            <BsTask className="type-icon test-icon" />
                                            <span className="type-label">
                                              Test
                                            </span>
                                          </span>
                                          <span
                                            className="item-name"
                                            title={test.name}
                                          >
                                            {test.name.length >
                                            (isMobile ? 20 : 40)
                                              ? `${test.name.substring(
                                                  0,
                                                  isMobile ? 20 : 40
                                                )}...`
                                              : test.name}
                                          </span>
                                          <span className="score-badge champion">
                                            Score: {test.obtainedPoints}/
                                            {test.totalPoints} (
                                            {testScore.toFixed(1)}%)
                                          </span>
                                        </div>
                                        <Button
                                          variant="outline-primary"
                                          className="action-button"
                                          onClick={() =>
                                            handleResourceClick(
                                              {
                                                id: test._id,
                                                name: test.name,
                                                type: "test",
                                                topic: {
                                                  id: topic._id,
                                                  name: topic.name,
                                                  subjectId: topic.subjectId,
                                                  score: topic.score,
                                                },
                                              },
                                              {
                                                id: topic._id,
                                                name: topic.name,
                                                subjectId: topic.subjectId,
                                              }
                                            )
                                          }
                                        >
                                          Reattempt
                                        </Button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                      </div>
                    </Card.Body>
                  </Card>
                ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Recommendations;
